import { Box, Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import SumOfAmountByDate from '../../../../components/Reports/SumOfAmountByDate';
import ReportsPieChart from '../../../../components/Reports/ReportsPieChart';
import { selectOvertimeReportData } from '../../../../store/ReportsDetailsSlice/selectors';

const CustomBox = styled(Box)(({ theme }) => ({
  '&.MuiBox-root': {
    padding: '16px',
    border: '1px solid #E1E1E1',
    paddingTop: '12px',
    paddingRight: '16px',
    paddingBottom: '20px',
    paddingLeft: '16px',
    borderRadius: '8px',
    '& h5': {
      fontSize: '20px',
      fontWeight: 700,
      marginBottom: '16px',
    },
  },
}));

const FORMAT_DATE_REGEX = /(\d{2})\/(\d{2})\/(\d{4})/;

const getData = (data, key) => {
  const totals = {};
  const isDateKey = key === 'date';
  const labelKey = isDateKey ? 'date' : 'label';
  const valueKey = isDateKey ? 'amount' : 'value';

  if (data.length === 0) return [];

  data.forEach((row) => {
    if (!row[key]) return console.error('No such key');

    let customKey = row[key] ?? 'Unknown';
    const amount = parseFloat(row.amount || '0');

    if (isDateKey) {
      customKey = customKey.replace(FORMAT_DATE_REGEX, '$3-$1-$2');
    }

    if (!totals[customKey]) {
      totals[customKey] = 0;
    }

    totals[customKey] += amount;
  });

  return Object.entries(totals)
    .map(([label, value]) => ({
      [labelKey]: label,
      [valueKey]: parseFloat(value.toFixed(2)),
    }))
    .sort((a, b) =>
      isDateKey ? new Date(a.date) - new Date(b.date) : b.value - a.value
    );
};

const _OvertimeGraphics = () => {
  const { overtimeReportData } = useSelector(selectOvertimeReportData);

  const flatOvertimeData = useMemo(() => {
    if (!overtimeReportData || !overtimeReportData.hasOwnProperty('data'))
      return [];

    return Object.values(overtimeReportData.data).flatMap((item) => item);
  }, [overtimeReportData]);

  const paycodeData = useMemo(
    () => getData(flatOvertimeData, 'pay_code'),
    [flatOvertimeData]
  );

  const reasonData = useMemo(
    () => getData(flatOvertimeData, 'reason'),
    [flatOvertimeData]
  );

  const dateData = useMemo(
    () => getData(flatOvertimeData, 'date'),
    [flatOvertimeData]
  );

  return (
    <Grid container spacing={2} className="my-2 px-2">
      {flatOvertimeData.length > 0 && (
        <Grid item xs={12}>
          <Box>
            <Grid container spacing={2} className="my-2">
              <Grid item xs={12}>
                <CustomBox>
                  <Typography variant="h5">Overtime Amount by Date</Typography>
                  <SumOfAmountByDate data={dateData} />
                </CustomBox>
              </Grid>
              <Grid item xs={12} lg={6}>
                <CustomBox>
                  <Typography variant="h5">
                    Overtime Amount by Reason
                  </Typography>
                  <ReportsPieChart data={reasonData} />
                </CustomBox>
              </Grid>
              <Grid item xs={12} lg={6}>
                <CustomBox>
                  <Typography variant="h5">
                    Overtime Amount by Pay Code
                  </Typography>
                  <ReportsPieChart data={paycodeData} />
                </CustomBox>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export const OvertimeGraphics = React.memo(_OvertimeGraphics);
